/**
 * How one report leaves the device (plan D48, T69). Never throws, never
 * retries, never reports its own failure — any of the three is an error loop.
 *
 * ── TWO WAYS OUT ────────────────────────────────────────────────────────────
 * - **Beacon, anonymous.** `navigator.sendBeacon(url, body)` with the body as
 *   a STRING goes out as `text/plain;charset=UTF-8`, a CORS-safelisted type
 *   with no preflight. A JSON-typed `Blob` would need a preflight, which a
 *   beacon cannot make, and the browser would drop the report in silence
 *   (T42). A beacon cannot set `Authorization` either, so it is anonymous.
 *   It is the only way that survives the page going away, so it is used
 *   whenever the page is unloading or hidden, and whenever there is no
 *   session.
 * - **`fetch` with `keepalive`, through the `ApiClient`,** while the page is
 *   alive and a session exists. The bearer (a preflight, answered by the
 *   API's CORS allowlist, `https://localhost` included) lets the route log the
 *   account id. If that request fails, nothing else is tried: the Worker may
 *   be down, and a second attempt is a retry.
 *
 * When `sendBeacon` is missing or refuses the body (it returns false when its
 * queue is full), a plain anonymous `fetch(…, { keepalive: true })` goes
 * instead. A beacon that returns true was only QUEUED; whatever the server
 * answers is never seen, and that is accepted.
 */

export type SendMode = "alive" | "unloading";

export interface SendPorts {
  /** The session-bearing path. Absent in a context with no `ApiClient`. */
  readonly authorized?: {
    hasSession(): Promise<boolean>;
    post(body: string): Promise<unknown>;
  };
  /** `navigator.sendBeacon`, bound; undefined where the browser has none. */
  readonly beacon: ((url: string, body: string) => boolean) | undefined;
  readonly fetch: (url: string, init: RequestInit) => Promise<unknown>;
  /** `POST /client-errors` under the API base (`clientErrorsUrl`). */
  readonly url: string;
}

/** The body is JSON; the type is text/plain so no request needs a preflight. */
export const REPORT_CONTENT_TYPE = "text/plain;charset=UTF-8";

export type SendReport = (body: string, mode: SendMode) => void;

const swallow = (): undefined => undefined;

export function createSendReport(ports: SendPorts): SendReport {
  const anonymous = (body: string): void => {
    try {
      if (ports.beacon?.(ports.url, body) === true) {
        return;
      }
    } catch {
      // A beacon that throws (a body over its limit, a torn-down page) falls
      // through to fetch, like one that returns false.
    }
    try {
      ports
        .fetch(ports.url, {
          body,
          credentials: "omit",
          headers: { "content-type": REPORT_CONTENT_TYPE },
          keepalive: true,
          method: "POST",
        })
        .catch(swallow);
    } catch {
      // No fetch at all: the report is lost, by design.
    }
  };

  const withSession = async (body: string): Promise<void> => {
    const { authorized } = ports;
    if (authorized === undefined) {
      anonymous(body);
      return;
    }
    let signedIn = false;
    try {
      signedIn = await authorized.hasSession();
    } catch {
      signedIn = false;
    }
    if (!signedIn) {
      anonymous(body);
      return;
    }
    // One attempt. A failure here is not followed by the anonymous path.
    await authorized.post(body);
  };

  return (body, mode) => {
    try {
      if (mode === "unloading") {
        anonymous(body);
        return;
      }
      withSession(body).catch(swallow);
    } catch {
      // Never let reporting become an error of its own.
    }
  };
}
